import DecimalInput from 'components/forms/inputs/rhf-inputs/DecimalInput';
import DynamicSelectInput from 'components/forms/inputs/rhf-inputs/DynamicSelectInput';
import InputWrapper from 'components/forms/inputs/rhf-inputs/InputWrapper';
import SelectInput from 'components/forms/inputs/rhf-inputs/SelectInput';
import TextInput from 'components/forms/inputs/rhf-inputs/TextInput';
import PartyName from 'components/party/PartyName';
import { activeCasesFilter } from 'components/tables/list-table/filter-pieces/filterConstants';
import { useEffect, useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useGetMattersListQuery, useLazyGetMattersListQuery } from 'store/api/matters';
import { ContactMinDto } from 'types/contacts';
import { FeatureFlag } from 'types/features';
import { MatterListItemMin } from 'types/matters';
import { t } from 'utils/getTranslations';
import { renderMatterIndicators, renderMatterOptionWithRestrictedSelection } from 'utils/matterOptionRenderer';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import { rejectedSubmitHandler } from 'utils/useRejectedSubmitCapture';
import { ModalFooter } from '../ModalFooter';
import { ModalHeader } from '../ModalHeader';

export interface AddNewPrepaidInvoiceForm {
    client: ContactMinDto | null;
    matter: { id: number; name: string } | null;
    prepaymentSum: string;
    readOnlyCurrency: string;
}

interface Props {
    onSubmit: (value: AddNewPrepaidInvoiceForm) => void;
    onClose: () => void;
    initialMatter?: MatterListItemMin;
}

const defaultValues: AddNewPrepaidInvoiceForm = {
    client: null,
    matter: null,
    prepaymentSum: '',
    readOnlyCurrency: '',
};

const AddNewPrepaidInvoiceFormModal = (props: Props) => {
    const { onClose, onSubmit, initialMatter } = props;
    const isPartyNumberUsageEnabled = useIsFeatureEnabled(FeatureFlag.PARTY_NUMBER_USAGE);

    const initialValues = useMemo(() => ({ ...defaultValues, matter: initialMatter ?? null }), [initialMatter]);

    const { handleSubmit, control, setValue } = useForm<AddNewPrepaidInvoiceForm>({
        defaultValues: initialValues,
        values: initialValues,
    });

    const [client, currentMatter] = useWatch({ control, name: ['client', 'matter'] });
    // Keyed on the id, never the object: every setValue hands useWatch a fresh copy of the matter, so
    // an effect depending on the object re-runs on its own writes below, without end.
    const currentMatterId = currentMatter?.id;

    const { data: currentMatterQueryData } = useGetMattersListQuery(
        { pageSize: 1, page: 0, filters: `id==${currentMatterId}` },
        { skip: !currentMatterId }
    );

    const currentMatterData = useMemo(() => {
        return currentMatterQueryData?.content.at(0);
    }, [currentMatterQueryData]);

    // TODO(react-doctor: no-cascading-set-state): RHF setValue cascade — framework
    // pattern syncing dependent form fields when the matter changes.
    useEffect(() => {
        if (!currentMatterId) {
            setValue('readOnlyCurrency', '');
            setValue('client', null);
        } else if (currentMatterData) {
            setValue('readOnlyCurrency', currentMatterData.pricelistCurrencyTypeCode);
            setValue('client', currentMatterData.clientParty);
        }
    }, [currentMatterData, setValue, currentMatterId]);

    const onSave = async () => {
        await handleSubmit((data) => {
            onSubmit(data);
            onClose();
        }, rejectedSubmitHandler('invoice-prepaid'))();
    };

    const clientInfo = useMemo(
        () =>
            client ? (
                <div className="flex flex-row flex-wrap">
                    <InputWrapper
                        name="official-name"
                        label={'official-name'}
                        readonly={true}
                        hasErrors={false}
                        noFullWidthInputWrapper
                        className="mr-8"
                    >
                        <>
                            {client.officialName ? (
                                <PartyName party={client} isLink useOfficialName variant="readonly-other" />
                            ) : (
                                '-'
                            )}
                        </>
                    </InputWrapper>
                    <InputWrapper
                        name="is-vat-liable"
                        label={'is-vat-liable'}
                        readonly={true}
                        hasErrors={false}
                        noFullWidthInputWrapper
                        className="mr-8"
                    >
                        <>{client.isVatLiable ? t('yes') : t('no')}</>
                    </InputWrapper>
                    {client.isVatLiable ? (
                        <InputWrapper
                            name="vat-number"
                            label={'vat-number'}
                            readonly={true}
                            hasErrors={false}
                            noFullWidthInputWrapper
                        >
                            <>{client.vatRegNum ?? '-'}</>
                        </InputWrapper>
                    ) : null}
                </div>
            ) : null,
        [client]
    );

    return (
        <>
            <ModalHeader title={t('invoices.new-prepayment-modal.title')} onCancel={onClose} />
            <div className="h-full overflow-y-auto px-8 py-[38px] max-sm:px-4 max-sm:py-6">
                <div className="flex flex-col gap-4">
                    <p className="text-body1">{t('invoices.new-prepayment-modal.description')}</p>
                    <DynamicSelectInput
                        setValue={setValue}
                        control={control}
                        label="invoices.new-prepayment-modal.case"
                        required
                        name="matter"
                        paginated={{
                            fetch: useLazyGetMattersListQuery,
                            searchField: ['fullName', 'clientParty.name', 'clientParty.partyNumber'],
                            filters: activeCasesFilter,
                            sort: ['clientParty.name,asc', 'name,asc'],
                        }}
                        groupByField="clientParty.name"
                        getOptionLabel="fullName"
                        getOptionIdentifier="id"
                        placeholder="timecards.form.select-case"
                        placeholderOnFocus="timecards.form.search-case-by"
                        getSelectedValueStartAdornment={renderMatterIndicators}
                        customOptionRenderer={(option, props, getValue) =>
                            renderMatterOptionWithRestrictedSelection(option, props, getValue)
                        }
                        getGroupStartAdornment={
                            isPartyNumberUsageEnabled
                                ? (_group, options) => {
                                      if (!options || options.length === 0) {
                                          return null;
                                      }
                                      const firstOption = options[0];
                                      if (!firstOption?.clientParty) {
                                          return null;
                                      }
                                      const partyNumber = firstOption.clientParty.partyNumber?.trim();
                                      if (partyNumber && partyNumber.length > 0) {
                                          return `[${partyNumber}]`;
                                      }
                                      return null;
                                  }
                                : undefined
                        }
                    />
                    <SelectInput
                        setValue={setValue}
                        control={control}
                        label="invoices.new-prepayment-modal.client"
                        name="client"
                        options={[] as ContactMinDto[]}
                        disabled
                        getOptionLabel="name"
                        getOptionIdentifier="id"
                        placeholder={' '}
                    />
                    {clientInfo}
                    <div className="flex flex-row gap-4">
                        <DecimalInput
                            name="prepaymentSum"
                            control={control}
                            label="invoices.new-prepayment-modal.prepayment-sum"
                            placeholder="invoices.new-prepayment-modal.prepayment-sum"
                            wrapperClassName="w-1/2"
                            required
                        />
                        <TextInput
                            name="readOnlyCurrency"
                            control={control}
                            label="invoices.new-prepayment-modal.currency"
                            disabled
                            wrapperClassName="w-1/2"
                        />
                    </div>
                </div>
            </div>
            <ModalFooter
                onCancel={onClose}
                onSave={() => {
                    void onSave();
                }}
            />
        </>
    );
};

export default AddNewPrepaidInvoiceFormModal;
